import type { LucideIcon } from "lucide-react";
import { cn } from "@/lib/utils";

/**
 * D08 consistency contract: the ONE status-chip treatment shared by dashboard,
 * workspace, grading queues, and exam surfaces. Callers supply the localized
 * label; per-domain tone/icon maps live next to the domain (see
 * features/assignments/lib/status-meta.ts). Do not hand-roll new pills.
 */
export type StatusTone = "neutral" | "info" | "success" | "warning" | "danger" | "brand" | "inverse";

const TONE_CLASS: Record<StatusTone, string> = {
  neutral: "border-neutral-200 bg-neutral-50 text-neutral-600",
  info: "border-sky-200 bg-sky-50 text-sky-700",
  success: "border-emerald-200 bg-emerald-50 text-emerald-700",
  warning: "border-amber-200 bg-amber-50 text-amber-800",
  danger: "border-red-200 bg-red-50 text-red-700",
  brand: "border-orange-200 bg-orange-50 text-[#E94D1D]",
  /**
   * `EX-DIV03`, admin's `EX-DIV02` tone (`docs/03` §6): a name rather than a
   * state, solid near-black with white text. The division tag ("Div. 3") is
   * its one use.
   */
  inverse: "border-neutral-950 bg-neutral-950 text-white",
};

/** Dark-surface variant for chips rendered on the exam shell / code panels. */
const TONE_CLASS_DARK: Record<StatusTone, string> = {
  neutral: "border-white/15 bg-white/5 text-white/70",
  info: "border-sky-400/30 bg-sky-400/10 text-sky-300",
  success: "border-emerald-400/30 bg-emerald-400/10 text-emerald-300",
  warning: "border-amber-400/30 bg-amber-400/10 text-amber-300",
  danger: "border-red-400/30 bg-red-400/10 text-red-300",
  brand: "border-orange-400/30 bg-orange-400/10 text-orange-300",
  /** `EX-DIV03`: the same name on the Judge's near-black, white with ink text. */
  inverse: "border-white bg-white text-neutral-950",
};

export interface StatusBadgeProps {
  tone: StatusTone;
  label: string;
  icon?: LucideIcon;
  /** Renders the dark-surface palette (exam shell, code panels). */
  onDark?: boolean;
  className?: string;
}

export function StatusBadge({ tone, label, icon: Icon, onDark = false, className }: StatusBadgeProps) {
  return (
    <span
      className={cn(
        "inline-flex items-center gap-1.5 whitespace-nowrap rounded-full border px-2.5 py-1 text-xs font-semibold",
        (onDark ? TONE_CLASS_DARK : TONE_CLASS)[tone],
        className
      )}
    >
      {Icon ? <Icon aria-hidden className="h-3.5 w-3.5 shrink-0" /> : null}
      {label}
    </span>
  );
}
